/**
 * Pure helpers for the dummy data profile's per-question settings. They
 * mirror what the generator does with the profile, so the screen can show
 * what a setting will produce before anything is generated.
 */
import { buildCatalogue, variantsOf } from "features/TabSpec/model/catalogue"
import { CatalogueQuestion } from "features/TabSpec/types/catalogue"
import { DummyProfile, DummyQuestionSettings } from "types/dummyData.types"

export interface PreviewOption {
  label: string
  exception: boolean
}

/** The largest number the generator takes for a minimum, maximum, average or spread. */
export const MAX_PROFILE_NUMBER = 1e12

/**
 * The generator's rule for a single answer: named options take their number,
 * unnamed ordinary options share what is left of 100 equally, and exceptions
 * count only when named. Returns percentages that add up to 100 (or all zero).
 */
export const sharePreview = (
  options: PreviewOption[],
  shares: Record<string, number> | undefined
): Record<string, number> => {
  const weights: Record<string, number> = {}
  if (!shares || Object.keys(shares).length === 0) {
    options.forEach((o) => (weights[o.label] = o.exception ? 0 : 1))
  } else {
    const named = options.reduce((sum, o) => sum + (shares[o.label] ?? 0), 0)
    const unnamed = options.filter(
      (o) => !o.exception && shares[o.label] === undefined
    ).length
    const rest = named < 100 && unnamed > 0 ? (100 - named) / unnamed : 0
    options.forEach((o) => {
      weights[o.label] =
        shares[o.label] !== undefined ? shares[o.label] : o.exception ? 0 : rest
    })
  }
  const total = Object.values(weights).reduce((a, b) => a + b, 0)
  const out: Record<string, number> = {}
  options.forEach(
    (o) =>
      (out[o.label] =
        total > 0 ? Math.round((weights[o.label] / total) * 1000) / 10 : 0)
  )
  return out
}

/** An empty object or list: a setting that says nothing. */
const isEmpty = (v: unknown): boolean =>
  v === undefined ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    Object.keys(v).length === 0)

/** Returns a new profile with one question's settings patched; empty settings are removed. */
export const setQuestion = (
  profile: DummyProfile,
  label: string,
  patch: Partial<DummyQuestionSettings>
): DummyProfile => {
  const current = profile.questions?.[label] ?? {}
  const next: DummyQuestionSettings = { ...current, ...patch }
  const keys = Object.keys(next) as (keyof DummyQuestionSettings)[]
  keys.forEach((k) => {
    if (isEmpty(next[k])) delete next[k]
  })
  const questions = { ...(profile.questions ?? {}) }
  if (Object.keys(next).length === 0) delete questions[label]
  else questions[label] = next
  return { ...profile, questions }
}

/** Returns a new profile without any settings for the question. */
export const removeQuestion = (
  profile: DummyProfile,
  label: string
): DummyProfile => {
  const questions = { ...(profile.questions ?? {}) }
  delete questions[label]
  return { ...profile, questions }
}

/** Labels the profile has settings for that are not among the questionnaire's questions. */
export const unusedLabels = (
  profile: DummyProfile,
  questionLabels: string[]
): string[] => {
  const known = new Set(questionLabels)
  return Object.keys(profile.questions ?? {})
    .filter((l) => !known.has(l))
    .sort()
}

/** One sample answer per line; blank lines are dropped. */
export const textsFromInput = (text: string): string[] =>
  text
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean)

/** What is wrong with a number question's settings, as the generator would refuse it; null when nothing is. */
export const numbersProblem = (s: DummyQuestionSettings): string | null =>
  s.min !== undefined && s.max !== undefined && s.min > s.max
    ? "The minimum is above the maximum."
    : null

/**
 * The questions of every variant of the questionnaire, each once (the first
 * by label), in questionnaire order: the first variant's questions, then
 * those only other variants have. The profile names questions by label for
 * every variant at once.
 */
export const profileQuestions = (parse: unknown): CatalogueQuestion[] => {
  const seen = new Map<string, CatalogueQuestion>()
  variantsOf(parse).forEach((variant) =>
    buildCatalogue({ parse, variant, codebook: null, routing: null }).questions.forEach(
      (q) => {
        if (!seen.has(q.label)) seen.set(q.label, q)
      }
    )
  )
  return [...seen.values()]
}
